import React from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin, Clock, Truck, Phone } from 'lucide-react';
import { useLocation } from '../../contexts/LocationContext';
import { useCookieConsent } from '../CookieBanner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';

const DAY_NAMES = {
  monday: 'Montag', tuesday: 'Dienstag', wednesday: 'Mittwoch',
  thursday: 'Donnerstag', friday: 'Freitag', saturday: 'Samstag', sunday: 'Sonntag'
};

const DAY_ORDER = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

function getTodayKey() {
  const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  return days[new Date().getDay()];
}

export default function LocationPicker() {
  const { locations, announcements, selectedLocation, selectLocation, showLocationPicker, setShowLocationPicker } = useLocation();
  const cookieConsented = useCookieConsent();
  const navigate = useNavigate();

  // Don't show on /meine-bestellungen
  const isMyOrdersPage = typeof window !== 'undefined' && window.location.pathname.startsWith('/meine-bestellungen');
  const todayKey = getTodayKey();

  // Block location picker if cookie banner is still showing
  const canShow = showLocationPicker && !isMyOrdersPage && cookieConsented;

  const handleSelectAndGo = (location) => {
    selectLocation(location);
    navigate(`/bestellen/${location.slug}`);
  };

  return (
    <Dialog open={canShow} onOpenChange={setShowLocationPicker}>
      <DialogContent className="sm:max-w-2xl p-0 gap-0 overflow-hidden" data-testid="location-picker-modal">
        <DialogHeader className="p-6 pb-2">
          <DialogTitle className="font-display text-2xl text-center">
            Wo möchtest du bestellen?
          </DialogTitle>
          <DialogDescription className="text-center text-muted-foreground">
            Wähle deinen Standort für Lieferung oder Abholung
          </DialogDescription>
        </DialogHeader>
        
        <div className="p-6 pt-4 space-y-4">
          {locations.map((location) => {
            const todayHours = location.opening_hours?.[todayKey];
            const isSelected = selectedLocation?.slug === location.slug;

            return (
              <button
                key={location.slug}
                onClick={() => handleSelectAndGo(location)}
                className={`w-full rounded-xl border-2 transition-all text-left overflow-hidden
                  ${isSelected
                    ? 'border-primary-500 bg-primary-500/10' 
                    : 'border-border hover:border-primary-300 bg-card'
                  }`}
                data-testid={`location-option-${location.slug}`}
              >
                <div className="p-5">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 space-y-3">
                      <h3 className="font-display text-xl font-bold">{location.name}</h3>
                      
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <MapPin className="w-4 h-4 text-primary-500 flex-shrink-0" />
                        <span>{location.address.street}, {location.address.postal_code} {location.address.city}</span>
                      </div>
                      
                      {location.phone && (
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Phone className="w-4 h-4 text-primary-500 flex-shrink-0" />
                          <span>{location.phone}</span>
                        </div>
                      )}

                      <div className="flex flex-wrap items-center gap-4 text-sm">
                        <div className="flex items-center gap-1.5">
                          <Clock className="w-4 h-4 text-primary-500 flex-shrink-0" />
                          {location.is_open_now ? (
                            <span className="text-green-500 font-medium">Jetzt geöffnet</span>
                          ) : (
                            <span className="text-muted-foreground">Geschlossen</span>
                          )}
                        </div>
                        
                        {todayHours && !todayHours.is_closed && (
                          <span className="text-muted-foreground">
                            Heute: {todayHours.open} - {todayHours.close} Uhr
                          </span>
                        )}
                        
                        <div className="flex items-center gap-1.5">
                          <Truck className="w-4 h-4 text-primary-500 flex-shrink-0" />
                          <span className="text-muted-foreground">{location.current_delivery_time || '30-45 Min'}</span>
                        </div>
                      </div>
                    </div>

                    <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-1 transition-colors
                      ${isSelected ? 'border-primary-500 bg-primary-500' : 'border-muted-foreground'}`}>
                      {isSelected && (
                        <svg className="w-3.5 h-3.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </div>
                  </div>
                </div>
              </button>
            );
          })}

          {/* Filialen in Vorbereitung: nur Ankündigung, nicht auswählbar */}
          {announcements.map((a) => (
            <div key={a.slug}
              className="w-full rounded-xl border-2 border-dashed border-amber-300 bg-amber-50/60 text-left overflow-hidden opacity-90 cursor-default"
              data-testid={`location-coming-soon-${a.slug}`}>
              <div className="p-5">
                <div className="flex items-center gap-2 mb-2">
                  <h3 className="font-display text-xl font-bold text-stone-800">{a.name}</h3>
                  <span className="px-2 py-0.5 rounded-full bg-amber-500 text-white text-[10px] font-bold uppercase tracking-wide">In Kürze</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-stone-600">
                  <MapPin className="w-4 h-4 text-amber-600 flex-shrink-0" />
                  <span>{a.address?.street}, {a.address?.postal_code} {a.address?.city}</span>
                </div>
                <p className="text-sm text-amber-700 font-medium mt-2">Eröffnung {a.eroeffnung_text} — noch nicht bestellbar</p>
              </div>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
